import React, { useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  InputAdornment,
} from '@material-ui/core';
import { Cancel, Done, Search } from '@material-ui/icons';
import axios, { Canceler } from 'axios';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { IUserModel } from 'interfaces';
import Select from 'components/OldSelect';
import { ISelectOption } from 'components/OldSelect/interfaces';
import { FormikSelectRefactorComponent } from 'components/Field';
import { getUsers } from '../api';
import { IPosition, IResponsiblePersonList } from '../interfaces';

interface IProps {
  responsiblePersonList: IResponsiblePersonList;
  disabled: boolean;
  positions: IPosition[];
  onClose(): void;
  onSubmitResponsiblePersonList(responsiblePersonList: IResponsiblePersonList): void;
}

const ResponsiblePersonListDialog: React.FC<IProps & DialogProps> = ({
  open,
  responsiblePersonList,
  positions,
  disabled,
  onSubmitResponsiblePersonList,
  onClose,
}) => {
  // Local state
  const cancelToken = useRef<Canceler | null>(null);

  const initialValues: IResponsiblePersonList = {
    headUser: null,
    headPosition: null,
    executorHeadUser: null,
    executorHeadPosition: null,
    executorUser: null,
    executorPosition: null,
    supervisor: null,
  };

  const validationSchema = yup.object().shape({
    headUser: yup.object().required('Обязательное поле').nullable(),
    headPosition: yup.object().required('Обязательное поле').nullable(),
    executorHeadUser: yup.object().required('Обязательное поле').nullable(),
    executorHeadPosition: yup.object().required('Обязательное поле').nullable(),
    executorUser: yup.object().required('Обязательное поле').nullable(),
    executorPosition: yup.object().required('Обязательное поле').nullable(),
    supervisor: yup.object().required('Обязательное поле').nullable(),
  });

  // Methods
  const handleSearchExecutors = (searchQuery: string) => {
    return new Promise<ISelectOption[]>(async (resolve, reject) => {
      if (cancelToken.current) cancelToken.current();

      try {
        const response = await getUsers(searchQuery, {
          cancelToken: new axios.CancelToken((cancel: Canceler) => (cancelToken.current = cancel)),
        });

        const executors = response.map((user: IUserModel) => {
          const { lastName, firstName, username } = user;
          return { label: `${lastName} ${firstName}`, value: username, ...user };
        });

        resolve(executors);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleSubmitResponsiblePersonList = (responsiblePersonList: IResponsiblePersonList) => {
    const {
      executorHeadPosition,
      executorHeadUser,
      executorPosition,
      executorUser,
      headPosition,
      headUser,
      supervisor,
    } = responsiblePersonList;

    onSubmitResponsiblePersonList({
      executorUserId: executorUser?.username,
      executorPositionId: executorPosition?.positionId,
      executorHeadUserId: executorHeadUser?.username,
      executorHeadPositionId: executorHeadPosition?.positionId,
      headUserId: headUser?.username,
      headPositionId: headPosition?.positionId,
      supervisorUserId: supervisor?.username,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="body"
      PaperProps={{ style: { overflow: 'initial' } }}
    >
      <DialogTitle>
        <b>{responsiblePersonList ? 'Обновить' : 'Назначить'} ответственных лиц</b>
      </DialogTitle>

      <Formik
        initialValues={responsiblePersonList ?? initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmitResponsiblePersonList}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            <DialogContent dividers style={{ overflow: 'initial' }}>
              <Grid container spacing={2}>
                <Grid item lg={6} xs={12}>
                  <Select
                    label="Инженер застройщика"
                    name="executorUser"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    value={{ label: values.executorUser?.fullName!, value: values.executorUser?.username! }}
                    disabled={disabled}
                    error={Boolean(touched.executorUser && errors.executorUser)}
                    helperText={touched.executorUser && errors.executorUser}
                    search={handleSearchExecutors}
                    options={[]}
                    onChange={(option) => setFieldValue('executorUser', option)}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Field
                    name="executorPosition"
                    label="Должность"
                    disabled={disabled}
                    options={positions}
                    onChange={(option: IPosition) => setFieldValue('executorPosition', option)}
                    getOptionLabel={(option: IPosition) => option.positionName}
                    getOptionSelected={(option: IPosition, value: IPosition) => option.positionId === value.positionId}
                    component={FormikSelectRefactorComponent}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Select
                    label="Инженер"
                    name="executorHeadUser"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    value={{ label: values.executorHeadUser?.fullName!, value: values.executorHeadUser?.username! }}
                    disabled={disabled}
                    error={Boolean(touched.executorHeadUser && errors.executorHeadUser)}
                    helperText={touched.executorHeadUser && errors.executorHeadUser}
                    search={handleSearchExecutors}
                    options={[]}
                    onChange={(option) => setFieldValue('executorHeadUser', option)}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Field
                    name="executorHeadPosition"
                    label="Должность"
                    disabled={disabled}
                    options={positions}
                    onChange={(option: IPosition) => setFieldValue('executorHeadPosition', option)}
                    getOptionLabel={(option: IPosition) => option.positionName}
                    getOptionSelected={(option: IPosition, value: IPosition) => option.positionId === value.positionId}
                    component={FormikSelectRefactorComponent}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Select
                    label="Главный инженер"
                    name="headUser"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    value={{ label: values.headUser?.fullName!, value: values.headUser?.username! }}
                    disabled={disabled}
                    error={Boolean(touched.headUser && errors.headUser)}
                    helperText={touched.headUser && errors.headUser}
                    search={handleSearchExecutors}
                    options={[]}
                    onChange={(option) => setFieldValue('headUser', option)}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Field
                    name="headPosition"
                    label="Должность"
                    disabled={disabled}
                    options={positions}
                    onChange={(option: IPosition) => setFieldValue('headPosition', option)}
                    getOptionLabel={(option: IPosition) => option.positionName}
                    getOptionSelected={(option: IPosition, value: IPosition) => option.positionId === value.positionId}
                    component={FormikSelectRefactorComponent}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <Select
                    label="Наблюдатель"
                    name="supervisor"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    value={{ label: values.supervisor?.fullName!, value: values.supervisor?.username! }}
                    disabled={disabled}
                    error={Boolean(touched.supervisor && errors.supervisor)}
                    helperText={touched.supervisor && errors.supervisor}
                    search={handleSearchExecutors}
                    options={[]}
                    onChange={(option) => setFieldValue('supervisor', option)}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant="contained" disabled={disabled} onClick={onClose} startIcon={<Cancel />}>
                Отменить
              </Button>

              <Button color="primary" variant="contained" disabled={disabled} type="submit" startIcon={<Done />}>
                {responsiblePersonList ? 'Обновить' : 'Назначить'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ResponsiblePersonListDialog;
